import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { Card, Col } from 'react-bootstrap';

import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import Button from 'components/DesignSystem/Button';
import Checkbox from 'components/DesignSystem/Checkbox';
import Snackbar from 'components/DesignSystem/Snackbar';
import DoubleSlider from 'components/DesignSystem/Slider/DoubleSlider';
import CentralizedSpinner from 'components/CentralizedSpinner';
import useChartVariable from 'hooks/Meteorology/Agro/WaterBalanceCulture/DetailedView/useChartVariable';
import useGraphData from 'hooks/Meteorology/Agro/WaterBalanceCulture/DetailedView/useGraphData';
import useParams from 'hooks/Meteorology/Agro/WaterBalanceCulture/DetailedView/useParams';
import useWaterBalanceChartSubtitle from 'hooks/Meteorology/Agro/useWaterBalanceChartSubtitle';
import useDownloadDivImage from 'hooks/useDownloadDivImage';
import DVChart from './DVChart';
import About from '../../About';

import './index.scss';

export default function DVCard() {
  const dvImages = useRef<HTMLDivElement>(null);
  const params = useParams();
  const {
    chartOptions,
    selectedChartVariables,
    allSelected,
    toggleChartVariables,
    selectAll,
    deselectAll,
    isLoading: variablesLoading,
  } = useChartVariable();
  const selectedVariableNames = selectedChartVariables
    .map((variableId) => chartOptions.find((o) => o.id === variableId)?.name)
    .filter((v) => v) as string[];
  const {
    graphData, runDate, queriedLocationId, isFetching: graphDataLoading, queryError, onRetry,
  } = useGraphData();
  const subtitle = useWaterBalanceChartSubtitle({
    series: graphData, runDate, locationId: queriedLocationId,
  });
  const charts = useMemo(() => (
    graphData?.map((data) => ({
      ...data, label: chartOptions.find((o) => o.name === data.name)?.label || '',
    }))
  ), [graphData, chartOptions]);

  const displayChartOptions = useMemo(
    () => chartOptions.filter(
      (option) => option.name !== 'chuva' && option.name !== 'temperatura',
    ),
    [chartOptions],
  );

  const chuvaOption = useMemo(
    () => chartOptions.find((o) => o.name === 'chuva'),
    [chartOptions],
  );
  const temperaturaOption = useMemo(
    () => chartOptions.find((o) => o.name === 'temperatura'),
    [chartOptions],
  );
  const hasCombinedOption = useMemo(
    () => !!chuvaOption && !!temperaturaOption,
    [chuvaOption, temperaturaOption],
  );

  const isCombinedSelected = useMemo(() => {
    if (!hasCombinedOption || !chuvaOption || !temperaturaOption) return false;
    return selectedChartVariables.includes(chuvaOption.id)
      && selectedChartVariables.includes(temperaturaOption.id);
  }, [
    selectedChartVariables,
    hasCombinedOption,
    chuvaOption,
    temperaturaOption,
  ]);

  const filteredVariableNames = useMemo(() => (
    selectedVariableNames.filter(
      (name) => name !== 'chuva' && name !== 'temperatura',
    )
  ), [selectedVariableNames]);

  const toggleCombinedVariables = useCallback(() => {
    if (!hasCombinedOption || !chuvaOption || !temperaturaOption) return;

    if (isCombinedSelected) {
      toggleChartVariables(chuvaOption.id);
      toggleChartVariables(temperaturaOption.id);
    } else {
      if (!selectedChartVariables.includes(chuvaOption.id)) {
        toggleChartVariables(chuvaOption.id);
      }
      if (!selectedChartVariables.includes(temperaturaOption.id)) {
        toggleChartVariables(temperaturaOption.id);
      }
    }
  }, [
    hasCombinedOption,
    isCombinedSelected,
    toggleChartVariables,
    chuvaOption,
    temperaturaOption,
    selectedChartVariables,
  ]);

  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd, setZoomEnd] = useState(100);

  const setZoom = useCallback(([start, end]: number[]) => {
    setZoomStart(start);
    setZoomEnd(end);
  }, [setZoomStart, setZoomEnd]);

  const downloadChartsAsImage = useDownloadDivImage(dvImages);
  const filename = `balanco_hidrico_cultura_${selectedVariableNames.join('_')}.png`;

  const chartsHidden = !charts
    || !params
    || (filteredVariableNames.length === 0 && !isCombinedSelected);
  const warningMessage = !charts
    ? 'Utilize o painel de controle para realizar uma consulta.'
    : 'Selecione uma variável.';

  const [showAbout, setShowAbout] = useState(false);

  useEffect(() => {
    if (charts && selectedVariableNames.length === 0) selectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts, selectAll]);

  if (variablesLoading || graphDataLoading) {
    return <CentralizedSpinner />;
  }

  if (queryError) {
    return (
      <QueryErrorState
        title={queryError.data}
        message={queryError.description}
        onRetry={onRetry}
      />
    );
  }

  return (
    <>
      <About
        show={showAbout}
        onHide={() => setShowAbout(false)}
      />
      <Card className="shadow-sm m-5 forecast-instruction-card">
        <Col className="flex-fill pt-5 h-100">
          <div className="d-flex align-items-center mb-4 px-5">
            <div className="d-flex flex-row dvcard-radios">
              {hasCombinedOption && (
                <Checkbox
                  key="chuva-temperatura"
                  label="Chuva e Temperatura"
                  onChange={toggleCombinedVariables}
                  checked={isCombinedSelected}
                  disabled={!charts || !params}
                />
              )}
              {displayChartOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  label={option.label}
                  onChange={() => { toggleChartVariables(option.id); }}
                  checked={selectedChartVariables.includes(option.id)}
                  disabled={!charts || !params}
                />
              ))}
              <Checkbox
                key="all"
                label="Todos"
                onChange={() => { if (allSelected) { deselectAll(); } else { selectAll(); } }}
                checked={allSelected}
                disabled={!charts || !params}
              />
            </div>
            <Button
              icon="download"
              small
              type="tertiary"
              onClick={downloadChartsAsImage(filename)}
              disabled={chartsHidden}
              id="chart-download-button"
              className="ms-auto"
            />
            <Button
              id="water-balance-about"
              className="ms-2 mt-auto"
              icon="info"
              onClick={() => setShowAbout(true)}
            />
          </div>
          {!chartsHidden && (
            <>
              <div className="agro-water-balance-culture-detailed-view-chart-container">
                <div ref={dvImages} className="h-auto w-auto">
                  {isCombinedSelected && charts && (() => {
                    const chuvaChart = charts.find((d) => d.name === 'chuva');
                    const temperaturaChart = charts.find((d) => d.name === 'temperatura');
                    if (!chuvaChart || !temperaturaChart) return null;
                    return (
                      <DVChart
                        key="chuva-temperatura"
                        zoomStart={zoomStart}
                        zoomEnd={zoomEnd}
                        combinedData={{
                          chuva: chuvaChart,
                          temperatura: temperaturaChart,
                        }}
                        subtitle={subtitle}
                      />
                    );
                  })()}
                  {filteredVariableNames.map((variableName) => (
                    <DVChart
                      key={variableName}
                      zoomStart={zoomStart}
                      zoomEnd={zoomEnd}
                      graphData={charts.find((d) => d.name === variableName)}
                      subtitle={subtitle}
                    />
                  ))}
                </div>
              </div>
              <div className="agro-detailed-view-x-slider">
                <DoubleSlider
                  min={0}
                  max={100}
                  value={[zoomStart, zoomEnd]}
                  setValue={setZoom}
                />
              </div>
            </>
          )}
          {chartsHidden && (
            <Snackbar className="my-4 mx-4">{warningMessage}</Snackbar>
          )}
        </Col>
      </Card>
    </>
  );
}
